import { Injectable } from '@angular/core';

export type FontScale = 0.875 | 1 | 1.125 | 1.25 | 1.5;

export interface AccessibilitySettings {
  fontScale: FontScale;
  grayscale: boolean;
  negativeContrast: boolean;
  highContrast: boolean;
  lightBackground: boolean;
  underlineLinks: boolean;
  readableFont: boolean;
}

const STORAGE_KEY = 'mobilize:a11y';

const DEFAULT_SETTINGS: AccessibilitySettings = {
  fontScale: 1,
  grayscale: false,
  negativeContrast: false,
  highContrast: false,
  lightBackground: false,
  underlineLinks: false,
  readableFont: false,
};

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function safeParse(json: string | null): unknown {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function toBoolean(v: unknown, fallback = false): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

function toFontScale(v: unknown): FontScale {
  const allowed: FontScale[] = [0.875, 1, 1.125, 1.25, 1.5];
  return allowed.includes(v as FontScale) ? (v as FontScale) : 1;
}

function normalizeSettings(input: unknown): AccessibilitySettings {
  if (!input || typeof input !== 'object') return { ...DEFAULT_SETTINGS };
  const o = input as Record<string, unknown>;
  return {
    fontScale: toFontScale(o['fontScale']),
    grayscale: toBoolean(o['grayscale']),
    negativeContrast: toBoolean(o['negativeContrast']),
    highContrast: toBoolean(o['highContrast']),
    lightBackground: toBoolean(o['lightBackground']),
    underlineLinks: toBoolean(o['underlineLinks']),
    readableFont: toBoolean(o['readableFont']),
  };
}

@Injectable({ providedIn: 'root' })
export class AccessibilityService {
  private _settings: AccessibilitySettings = { ...DEFAULT_SETTINGS };

  constructor() {
    this._settings = this.readFromStorage();
    this.applyToDocument();
  }

  get settings(): AccessibilitySettings {
    return this._settings;
  }

  setSettings(next: AccessibilitySettings): void {
    this._settings = { ...next };
    this.writeToStorage();
    this.applyToDocument();
  }

  patchSettings(patch: Partial<AccessibilitySettings>): void {
    this.setSettings({ ...this._settings, ...patch });
  }

  reset(): void {
    this.setSettings({ ...DEFAULT_SETTINGS });
  }

  increaseFont(): void {
    const steps: FontScale[] = [0.875, 1, 1.125, 1.25, 1.5];
    const idx = steps.indexOf(this._settings.fontScale);
    const next = steps[Math.min(steps.length - 1, Math.max(0, idx + 1))];
    this.patchSettings({ fontScale: next });
  }

  decreaseFont(): void {
    const steps: FontScale[] = [0.875, 1, 1.125, 1.25, 1.5];
    const idx = steps.indexOf(this._settings.fontScale);
    const next = steps[Math.max(0, Math.min(steps.length - 1, idx - 1))];
    this.patchSettings({ fontScale: next });
  }

  toggle<K extends keyof Omit<AccessibilitySettings, 'fontScale'>>(key: K): void {
    const current = this._settings[key];
    this.patchSettings({ [key]: !current } as Pick<AccessibilitySettings, K>);
  }

  private readFromStorage(): AccessibilitySettings {
    if (!isBrowser()) return { ...DEFAULT_SETTINGS };
    const raw = safeParse(window.localStorage.getItem(STORAGE_KEY));
    return normalizeSettings(raw);
  }

  private writeToStorage(): void {
    if (!isBrowser()) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this._settings));
    } catch {
      // ignore storage quota / privacy mode issues
    }
  }

  private applyToDocument(): void {
    if (!isBrowser()) return;

    const root = document.documentElement;
    root.style.setProperty('--a11y-font-scale', String(this._settings.fontScale));

    root.classList.toggle('a11y-grayscale', this._settings.grayscale);
    root.classList.toggle('a11y-negative', this._settings.negativeContrast);
    root.classList.toggle('a11y-high-contrast', this._settings.highContrast);
    root.classList.toggle('a11y-light-bg', this._settings.lightBackground);
    root.classList.toggle('a11y-underline-links', this._settings.underlineLinks);
    root.classList.toggle('a11y-readable-font', this._settings.readableFont);
  }
}

